/**
 * Minimal fixed-pitch terminal (web-client.md §5): the body is one DOM node per row (monospace,
 * `ch`-unit layout), diffed by per-row string compare so a redraw only touches changed rows. The
 * input bar is a real `<input>` (IME/RU input works for free — we never synthesize key events).
 *
 * The body holds two panes: the main column and an optional side rail (the chat panel). They are two
 * DOM columns rather than one column of composed text because a row carries exactly one style class,
 * and chat lines need their own (`me` / `peer`) next to whatever the main column is showing. Both
 * panes live inside the `role="log"` body, so the scrollback still reads as one region.
 *
 * This is DOM-bound UI (the DOM-free half lives in `core/`). It renders text lines and emits the
 * submitted line; it knows nothing about the protocol or the network.
 */
export interface TermLine {
  text: string;
  /** Optional style class (e.g. 'sys', 'me', 'peer', 'dim', 'err'). */
  cls?: string;
  /**
   * The command this row dispatches when clicked (session-chrome.md §2: every click IS a command).
   * A row with one is rendered as a real button, so the mouse is sugar over the typed path rather
   * than a second protocol - and the keyboard reaches it for free.
   */
  command?: string;
}

/**
 * The persistent frame around the scrollback (session-chrome.md §3.1): the breadcrumb and the tabs.
 *
 * It lives OUTSIDE the `role="log"` body, which is the structural half of the fix: chrome that
 * re-renders inside a live region makes a screen reader hear the board repaint as new output, and
 * "persistent" is not persistent if it scrolls away with the log.
 */
export interface TermChrome {
  /** One row answering "where am I": identity, seat, clock, connection. */
  breadcrumb: string;
  /** The panel tabs, in order. */
  tabs: readonly { key: string; label: string; active: boolean; unread: boolean }[];
  /** The single most relevant next move, shown above the input bar. */
  context: string;
}

/** The longest prefix every candidate shares - what a Tab with several matches extends to. */
function longestCommonPrefix(items: readonly string[]): string {
  if (items.length === 0) return '';
  let prefix = items[0]!;
  for (const item of items.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < item.length && prefix[i] === item[i]) i++;
    prefix = prefix.slice(0, i);
    if (!prefix) break;
  }
  return prefix;
}

export class Terminal {
  /** How many candidates a suggestion row shows before it says "+N" instead. */
  private static readonly MaxSuggestions = 8;

  readonly root: HTMLElement;
  private readonly header: HTMLElement;
  private readonly breadcrumb: HTMLElement;
  private readonly tabs: HTMLElement;
  private readonly contextRow: HTMLElement;
  private readonly body: HTMLElement;
  private readonly main: HTMLElement;
  private readonly side: HTMLElement;
  private readonly promptEl: HTMLElement;
  private readonly input: HTMLInputElement;
  private rows: TermLine[] = [];
  private sideRows: TermLine[] = [];
  private submitHandler: (text: string) => void = () => {};

  // Completion (clients/tab-completion.md). The terminal owns the KEYSTROKES and knows nothing about
  // commands: it asks for candidates and draws them, and the view decides what they are.
  private completer: (text: string, caret: number) => { items: string[]; from: number } = () => ({
    items: [],
    from: 0,
  });

  private candidates: string[] = [];
  private candidateIndex = -1;
  private candidateFrom = 0;
  private candidateRow: TermLine | null = null;
  private tabSignature = '';

  constructor(mount: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'ts-term';

    // ── Layer 1 (session-chrome.md §3.2): breadcrumb + tabs, OUTSIDE the log.
    this.header = document.createElement('div');
    this.header.className = 'ts-header';
    this.header.hidden = true;

    this.breadcrumb = document.createElement('div');
    this.breadcrumb.className = 'ts-breadcrumb';
    // Read on focus, never announced on repaint: this is orientation, not news.
    this.breadcrumb.setAttribute('aria-live', 'off');

    this.tabs = document.createElement('div');
    this.tabs.className = 'ts-tabs';
    // Real tabs justify real ARIA. The restraint in tab-completion §2.4 was about not claiming a
    // widget that did not exist; this one does.
    this.tabs.setAttribute('role', 'tablist');
    this.header.append(this.breadcrumb, this.tabs);

    // The tablist keyboard pattern: arrows move within it, Enter/Space activates (the button does that
    // for free), Escape or activation returns focus to the input - so the chrome is never a trap.
    this.tabs.addEventListener('keydown', (e) => {
      const tabs = Array.from(this.tabs.children) as HTMLElement[];
      const at = tabs.indexOf(document.activeElement as HTMLElement);
      if (at < 0) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const step = e.key === 'ArrowRight' ? 1 : -1;
        this.focusTab((at + step + tabs.length) % tabs.length);
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        this.input.focus();
      }
    });

    this.body = document.createElement('div');
    this.body.className = 'ts-body';
    // The scrollback is a polite live region so a screen reader announces new lines (WCAG 2.2 AA).
    this.body.setAttribute('role', 'log');
    this.body.setAttribute('aria-live', 'polite');
    this.body.setAttribute('aria-atomic', 'false');

    this.main = document.createElement('div');
    this.main.className = 'ts-main';
    this.side = document.createElement('div');
    this.side.className = 'ts-side';
    this.side.hidden = true;
    // Named so a screen reader announces which column it landed in; the rail is a region of the log,
    // not a second log.
    this.side.setAttribute('aria-label', 'Chat');
    this.body.append(this.main, this.side);

    // Delegated, because the diff RECYCLES row nodes: a listener per row would leak with every
    // repaint and fire the previous row's command after a frame changed underneath it.
    for (const pane of [this.main, this.side]) {
      pane.addEventListener('click', (e) => this.activate(e.target));
      pane.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (this.activate(e.target)) e.preventDefault();
        }
      });
    }

    // ── Layer 3 (§3.4): the one next move, permanently visible above the input.
    this.contextRow = document.createElement('div');
    this.contextRow.className = 'ts-context';
    this.contextRow.hidden = true;

    const bar = document.createElement('form');
    bar.className = 'ts-bar';

    this.promptEl = document.createElement('label');
    this.promptEl.className = 'ts-prompt';
    this.promptEl.textContent = '>';
    const inputId = 'ts-input';
    this.promptEl.setAttribute('for', inputId);

    this.input = document.createElement('input');
    this.input.className = 'ts-input';
    this.input.id = inputId;
    this.input.type = 'text';
    this.input.autocomplete = 'off';
    this.input.setAttribute('aria-label', 'Terminal input');
    this.input.autocapitalize = 'off';
    this.input.spellcheck = false;

    // Tab is intercepted ONLY on a non-empty input (§2.4). On an empty line it does what Tab does
    // everywhere - moves focus - which is what keyboard-only users need and what every terminal
    // emulator these players arrive from already does. There is nothing to complete on an empty line,
    // so the capability costs nothing and the escape hatch stays open.
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.candidates.length) {
        e.preventDefault();
        this.clearCandidates();
        return;
      }

      // On an EMPTY input the keyboard belongs to the chrome (session-chrome.md §4). Digits switch
      // panels; Tab moves focus to the tablist - which is what finally gives the empty-input Tab rule
      // from tab-completion §2.4 a destination instead of just an escape hatch.
      if (this.input.value.length === 0) {
        this.resetCycle();

        const digit = Number(e.key);
        if (Number.isInteger(digit) && digit >= 1 && digit <= this.tabs.childElementCount) {
          e.preventDefault();
          (this.tabs.children[digit - 1] as HTMLElement).click();
          return;
        }

        if (e.key === 'Tab' && !e.shiftKey && this.tabs.childElementCount > 0) {
          e.preventDefault();
          this.focusTab(0);
        }

        return;
      }

      if (e.key !== 'Tab') {
        this.resetCycle();
        return;
      }

      e.preventDefault();
      this.onTab(e.shiftKey);
    });

    // Live suggestions. Tab is how you ACCEPT a completion; it should not also be how you discover one
    // exists. Typing `/decide comm` shows the ids that match while you type, so a player learns what the
    // session holds without having to guess that a key does something.
    this.input.addEventListener('input', () => {
      this.resetCycle();
      this.suggest();
    });

    bar.append(this.promptEl, this.input);
    bar.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.input.value;
      this.input.value = '';
      this.clearCandidates();
      this.submitHandler(text);
    });

    this.root.append(this.header, this.body, this.contextRow, bar);
    mount.appendChild(this.root);
  }

  /** Render the current frame. Only rows whose text/class changed touch the DOM. */
  render(lines: TermLine[], side: TermLine[] = []): void {
    const split = side.length > 0;
    this.body.classList.toggle('ts-split', split);
    this.side.hidden = !split;

    this.rows = this.paint(this.main, this.rows, lines);
    this.sideRows = this.paint(this.side, this.sideRows, side);

    // Keep the newest output in view. Split, each pane scrolls on its own, so a chat line arriving
    // does not drag the board out from under whoever is reading it.
    this.main.scrollTop = this.main.scrollHeight;
    this.side.scrollTop = this.side.scrollHeight;
    this.body.scrollTop = this.body.scrollHeight;
  }

  /** Diff one pane against its previous frame, and return the frame just painted. */
  private paint(pane: HTMLElement, prevRows: TermLine[], lines: TermLine[]): TermLine[] {
    // Grow / shrink the row set.
    while (pane.childElementCount < lines.length) {
      const row = document.createElement('div');
      row.className = 'ts-row';
      pane.appendChild(row);
    }
    while (pane.childElementCount > lines.length) {
      pane.lastElementChild?.remove();
    }
    for (let i = 0; i < lines.length; i++) {
      const prev = prevRows[i];
      const next = lines[i]!;
      if (prev && prev.text === next.text && prev.cls === next.cls) continue;
      const row = pane.children[i] as HTMLElement;
      // Non-breaking render of blank lines so empty rows keep their height.
      row.textContent = next.text.length ? next.text : ' ';
      row.className = next.cls ? `ts-row ts-${next.cls}` : 'ts-row';

      // A row carrying a command becomes a real button: focusable, Enter/Space activated, announced
      // as a button - no capability is pointer-only (§4).
      if (next.command) {
        row.setAttribute('role', 'button');
        row.setAttribute('tabindex', '0');
        row.classList.add('ts-actionable');
        row.dataset.command = next.command;
      } else {
        row.removeAttribute('role');
        row.removeAttribute('tabindex');
        row.classList.remove('ts-actionable');
        delete row.dataset.command;
      }
    }
    return lines.slice();
  }

  /**
   * Shows what still matches, as the player types (§2.3 - the candidate row, live).
   *
   * Only ever a hint: it never rewrites the input, so a fast typist is never fighting it. The row is
   * capped because a session with thirty backlog items would otherwise answer a keystroke with a wall.
   *
   * The diffed renderer means an UNCHANGED row is not re-emitted, which is what keeps the polite live
   * region from re-announcing the same candidates on every keystroke.
   */
  private suggest(): void {
    const caret = this.input.selectionStart ?? this.input.value.length;
    const { items } = this.completer(this.input.value, caret);

    // Nothing to say, or nothing left to choose: one exact match is not a suggestion, it is a spoiler.
    if (items.length === 0 || (items.length === 1 && items[0] === this.tokenAt(caret))) {
      this.candidates = [];
      if (this.candidateRow) {
        this.candidateRow = null;
        this.paintCandidates();
      }
      return;
    }

    const shown = items.slice(0, Terminal.MaxSuggestions);
    const more = items.length - shown.length;
    const text = `  ${shown.join(' · ')}${more > 0 ? ` … +${more}` : ''}`;

    this.candidates = items;
    if (this.candidateRow?.text !== text) {
      this.candidateRow = { text, cls: 'dim' };
      this.paintCandidates();
    }
  }

  /** The token under the caret - what a candidate would replace. */
  private tokenAt(caret: number): string {
    const head = this.input.value.slice(0, caret);
    return head.slice(head.lastIndexOf(' ') + 1);
  }

  /**
   * Draws Layer 1 and Layer 3 (§3.1). Called with `null` on the views that have no session to be
   * oriented in, which hides the chrome rather than drawing an empty frame.
   */
  renderChrome(chrome: TermChrome | null): void {
    this.header.hidden = chrome === null;
    this.contextRow.hidden = chrome === null || chrome.context.length === 0;
    if (!chrome) {
      return;
    }

    if (this.breadcrumb.textContent !== chrome.breadcrumb) {
      this.breadcrumb.textContent = chrome.breadcrumb;
    }

    if (this.contextRow.textContent !== chrome.context) {
      this.contextRow.textContent = chrome.context;
    }

    // Tabs are rebuilt only when their shape changes; the common case is one class flipping.
    const signature = chrome.tabs.map((t) => `${t.key}:${t.label}`).join('|');
    if (this.tabSignature !== signature) {
      this.tabSignature = signature;
      this.tabs.replaceChildren(
        ...chrome.tabs.map((tab, i) => {
          const el = document.createElement('button');
          el.type = 'button';
          el.className = 'ts-tab';
          el.setAttribute('role', 'tab');
          el.dataset.key = tab.key;
          // The digit is the keyboard shortcut AND the label's own documentation.
          el.textContent = `${i + 1} ${tab.label}`;
          // Roving tabindex: one stop for the whole tablist, arrows move inside it.
          el.setAttribute('tabindex', i === 0 ? '0' : '-1');
          el.addEventListener('click', () => this.dispatch(`/view ${tab.key}`));
          return el;
        }),
      );
    }

    for (const el of Array.from(this.tabs.children) as HTMLElement[]) {
      const tab = chrome.tabs.find((t) => t.key === el.dataset.key);
      if (!tab) continue;
      el.setAttribute('aria-selected', String(tab.active));
      el.classList.toggle('is-active', tab.active);
      el.classList.toggle('is-unread', tab.unread);
    }
  }

  /** Moves keyboard focus onto one tab, keeping roving tabindex honest. */
  private focusTab(index: number): void {
    const tabs = Array.from(this.tabs.children) as HTMLElement[];
    tabs.forEach((tab, i) => tab.setAttribute('tabindex', i === index ? '0' : '-1'));
    tabs[index]?.focus();
  }

  /** Fires the command on a clicked/activated row, if it has one. */
  private activate(target: EventTarget | null): boolean {
    const command = (target as HTMLElement | null)?.dataset?.command;
    if (!command) {
      return false;
    }

    this.dispatch(command);
    return true;
  }

  /**
   * Runs a command as if it had been typed, echoing it first (§2: every click IS a command).
   *
   * The echo is the teaching half: a player who clicks a seat sees `/claim engineer` appear in the
   * log and learns the typed form, so the mouse never becomes a separate, undocumented protocol.
   */
  private dispatch(command: string): void {
    this.submitHandler(command);
    this.input.focus();
  }

  /** Where candidates come from. Set once by the shell; swapped when the view changes. */
  onComplete(completer: (text: string, caret: number) => { items: string[]; from: number }): void {
    this.completer = completer;
  }

  /**
   * Tab: complete when there is one answer, extend to the shared prefix when there are several, and
   * cycle in place on a second press (§2.3). Readline, not a dropdown - the candidates are a row.
   */
  private onTab(back: boolean): void {
    // Cycling: a second Tab walks the list already on screen rather than recomputing it.
    if (this.candidates.length > 1) {
      const step = back ? -1 : 1;
      this.candidateIndex =
        (this.candidateIndex + step + this.candidates.length) % this.candidates.length;
      this.put(this.candidates[this.candidateIndex]!);
      return;
    }

    const caret = this.input.selectionStart ?? this.input.value.length;
    const { items, from } = this.completer(this.input.value, caret);
    if (items.length === 0) {
      this.clearCandidates();
      return;
    }

    this.candidateFrom = from;
    if (items.length === 1) {
      this.candidates = [];
      this.candidateIndex = -1;
      // A single answer completes with a trailing space: the next argument is the point.
      this.put(items[0]!, true);
      this.clearCandidates();
      return;
    }

    const shared = longestCommonPrefix(items);
    const token = this.input.value.slice(from, caret);
    if (shared.length > token.length) {
      this.put(shared);
    }

    this.candidates = items;
    this.candidateIndex = -1;
    this.candidateRow = { text: `  ${items.join(' · ')}`, cls: 'dim' };
    this.paintCandidates();
  }

  /** Replaces the token under the caret with `value`. */
  private put(value: string, trailingSpace = false): void {
    const caret = this.input.selectionStart ?? this.input.value.length;
    const tail = this.input.value.slice(caret);
    const next = this.input.value.slice(0, this.candidateFrom) + value + (trailingSpace ? ' ' : '');
    this.input.value = next + tail;
    const at = next.length;
    this.input.setSelectionRange(at, at);
  }

  private resetCycle(): void {
    this.candidateIndex = -1;
  }

  private clearCandidates(): void {
    this.candidates = [];
    this.candidateIndex = -1;
    if (this.candidateRow) {
      this.candidateRow = null;
      this.paintCandidates();
    }
  }

  /**
   * Draws the candidate row as the last line of the main pane. It is a ROW, not a widget: the
   * one-node-per-row renderer stays untouched, the diffed frame handles it like any other line, and the
   * existing polite live region announces it to a screen reader without anyone claiming a listbox.
   */
  private paintCandidates(): void {
    const frame = this.candidateRow ? [...this.rows, this.candidateRow] : this.rows;
    this.rows = this.paint(this.main, this.rows, frame);
    this.main.scrollTop = this.main.scrollHeight;
  }

  setPrompt(prompt: string): void {
    this.promptEl.textContent = prompt;
  }

  onSubmit(handler: (text: string) => void): void {
    this.submitHandler = handler;
  }

  focus(): void {
    this.input.focus();
  }
}
